import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import AppLoader from './components/AppLoader'
import { useAuthStore } from './hooks/useAuthStore'
import AccountLayout from './layouts/AccountLayout'
import AppLayout from './layouts/AppLayout'
import AuthLayout from './layouts/AuthLayout'
import CompanyLayout from './layouts/CompanyLayout'
import ContainerLayout from './layouts/ContainerLayout'
import HeaderLayout from './layouts/HeaderLayout'
import InvitationsLayout from './layouts/InvitationsLayout'
import WorkspaceLayout from './layouts/WorkspaceLayout'
import { PrivateRoute } from './routes/PrivateRoute'

// Lazy load all view components
const AccessDenied = lazy(() => import('./views/auth/AccessDenied'))
const SignUpConfirm = lazy(() => import('./views/auth/ConfirmSignUp'))
const ForgotPassword = lazy(() => import('./views/auth/ForgotPassword'))
const ForgotPasswordConfirm = lazy(
  () => import('./views/auth/ForgotPasswordConfirm')
)
const SignIn = lazy(() => import('./views/auth/SignIn'))
const SignUp = lazy(() => import('./views/auth/SignUp'))
const Companies = lazy(() => import('./views/companies/Companies'))
const CompaniesAcceptInvitation = lazy(
  () => import('./views/companies/CompaniesAcceptInvitation')
)
const CompaniesCreateInvitation = lazy(
  () => import('./views/companies/CompaniesCreateInvitation')
)
const CompaniesDirectory = lazy(() =>
  import('./views/companies/CompaniesDirectory').then(module => ({
    default: module.CompaniesDirectory
  }))
)
const CompaniesEdit = lazy(() => import('./views/companies/CompaniesEdit'))
const CompaniesMembers = lazy(
  () => import('./views/companies/CompaniesMembers')
)
const CompaniesProfile = lazy(
  () => import('./views/companies/CompaniesProfile')
)
const CompaniesReceivedInvitations = lazy(
  () => import('./views/companies/CompaniesReceivedInvitations')
)
const CompaniesRoles = lazy(() => import('./views/companies/CompaniesRoles'))
const CompaniesSentInvitations = lazy(
  () => import('./views/companies/CompaniesSentInvitations')
)
const CompaniesWorkspaces = lazy(
  () => import('./views/companies/CompaniesWorkspaces')
)
const CompaniesCreate = lazy(() => import('./views/companies/CreateCompany'))
const CreateRole = lazy(() => import('./views/CreateRole'))
const Invitations = lazy(() => import('./views/Invitations'))
const Notifications = lazy(() => import('./views/Notifications'))
const CompleteProfile = lazy(() => import('./views/profile/CompleteProfile'))
const Profile = lazy(() => import('./views/profile/Profile'))
const UpdateRole = lazy(() => import('./views/UpdateRole'))
const CreateWorkspace = lazy(() => import('./views/workspaces/CreateWorkspace'))
const WorkspaceEdit = lazy(() => import('./views/workspaces/WorkspaceEdit'))
const WorkspaceMembers = lazy(
  () => import('./views/workspaces/WorkspaceMembers')
)
const WorkspaceSentInvitations = lazy(
  () => import('./views/workspaces/WorkspaceSentInvitations')
)
const MaterialReception = lazy(() => import('./views/assets/MaterialReception'))
const AssetDefinitions = lazy(() => import('./views/assets/AssetDefinitions'))
const CreateAssetDefinition = lazy(
  () => import('./views/assets/CreateAssetDefinition')
)
const UpdateAssetDefinition = lazy(
  () => import('./views/assets/UpdateAssetDefinition')
)
const CreateAsset = lazy(() => import('./views/assets/CreateAsset'))
const AssetDetail = lazy(() => import('./views/assets/AssetDetail'))
const AssetPassport = lazy(() => import('./views/public/AssetPassport'))
const PublicSearch = lazy(() => import('./views/public/PublicSearch'))
const PublicLayout = lazy(() => import('./layouts/PublicLayout'))
const DigitalTwin = lazy(() => import('./views/workspaces/DigitalTwin'))
const Inspections = lazy(() => import('./views/inspections/Inspections'))
const ConstructionLog = lazy(
  () => import('./views/construction-log/ConstructionLog')
)
const ChapterDetail = lazy(
  () => import('./views/construction-log/ChapterDetail')
)
const EntryDetail = lazy(() => import('./views/construction-log/EntryDetail'))
const ConstructionLogSettings = lazy(
  () => import('./views/construction-log/ConstructionLogSettings')
)
const ConstructionLogExport = lazy(
  () => import('./views/construction-log/ConstructionLogExport')
)

export const Router = () => {
  const { idToken } = useAuthStore()

  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to='/sign-in' />} />

          <Route element={<AuthLayout />}>
            <Route path='/sign-up' element={<SignUp />} />
            <Route path='/sign-up/confirm' element={<SignUpConfirm />} />
            <Route path='/forgot-password' element={<ForgotPassword />} />
            <Route
              path='/forgot-password/confirm'
              element={<ForgotPasswordConfirm />}
            />
            <Route path='/sign-in' element={<SignIn />} />
          </Route>

          <Route element={<PrivateRoute />}>
            <Route element={<AuthLayout />}>
              <Route path='/complete-profile' element={<CompleteProfile />} />
            </Route>

            <Route element={<WorkspaceLayout />}>
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/digital-twin'
                element={<DigitalTwin />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/inspections'
                element={<Inspections />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/materials'
                element={<MaterialReception />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/assets'
                element={<MaterialReception />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/assets/create'
                element={<CreateAsset />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/assets/:assetId'
                element={<AssetDetail />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/asset-definitions'
                element={<AssetDefinitions />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/asset-definitions/create'
                element={<CreateAssetDefinition />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/asset-definitions/:definitionId'
                element={<UpdateAssetDefinition />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/construction-log'
                element={<ConstructionLog />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/construction-log/chapters/:chapterId'
                element={<ChapterDetail />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/construction-log/entries/:entryId'
                element={<EntryDetail />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/construction-log/settings'
                element={<ConstructionLogSettings />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/construction-log/export'
                element={<ConstructionLogExport />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/edit'
                element={<WorkspaceEdit />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/members'
                element={<WorkspaceMembers />}
              />
              <Route
                path='/companies/:companyId/workspaces/:workspaceId/invitations/sent'
                element={<WorkspaceSentInvitations />}
              />
            </Route>

            <Route element={<HeaderLayout />}>
              <Route path='/companies/create' element={<CompaniesCreate />} />
              <Route path='/profile' element={<Profile />} />

              <Route
                path='/companies/:companyId/profile'
                element={<CompaniesProfile />}
              />
              <Route
                path='/companies/:companyId/workspaces/create'
                element={<CreateWorkspace />}
              />
              <Route path='/companies/:companyId' element={<CompanyLayout />}>
                <Route
                  path='/companies/:companyId/workspaces'
                  element={<CompaniesWorkspaces />}
                />
                <Route
                  path='/companies/:companyId/members'
                  element={<CompaniesMembers />}
                />
                <Route
                  path='/companies/:companyId/roles'
                  element={<CompaniesRoles />}
                />
                <Route element={<InvitationsLayout />}>
                  <Route
                    path='/companies/:companyId/invitations/sent'
                    element={<CompaniesSentInvitations />}
                  />
                  <Route
                    path='/companies/:companyId/invitations/received'
                    element={<CompaniesReceivedInvitations />}
                  />
                </Route>
              </Route>

              <Route
                path='/companies/:companyId/roles/create'
                element={<CreateRole />}
              />
              <Route
                path='/companies/:companyId/roles/:roleId'
                element={<UpdateRole readOnly />}
              />
              <Route
                path='/companies/:companyId/roles/:roleId/edit'
                element={<UpdateRole />}
              />

              <Route
                path='/companies/:companyId/invitations/received/:userId'
                element={<CompaniesAcceptInvitation />}
              />
              <Route
                path='/companies/:companyId/invitations/create'
                element={<CompaniesCreateInvitation />}
              />

              <Route element={<ContainerLayout />}>
                <Route path='/complete-profile' element={<CompleteProfile />} />

                <Route element={<AccountLayout />}>
                  <Route path='/companies' element={<Companies />} />
                  <Route path='/invitations' element={<Invitations />} />
                  <Route
                    path='/companies/directory'
                    element={<CompaniesDirectory />}
                  />
                </Route>
                <Route
                  path='/companies/:companyId/edit'
                  element={<CompaniesEdit />}
                />

                <Route path='/notifications' element={<Notifications />} />
              </Route>
            </Route>
          </Route>

          <Route element={<PublicLayout />}>
            <Route path='/search' element={<PublicSearch />} />
            <Route path='/assets/:assetId' element={<AssetPassport />} />
          </Route>

          <Route path='/access-denied' element={<AccessDenied />} />
          <Route
            path='*'
            element={<Navigate to={idToken ? '/companies' : '/sign-in'} />}
          />
        </Route>
      </Routes>
    </Suspense>
  )
}
